<template>
  <v-container>
    <div
      v-if="loading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        indeterminate
        color="#2661FF"
        size="80"
      ></v-progress-circular>
    </div>
    <div v-else>
      <v-row class="px-2">
        <v-col cols="12" md="6">
          <h2
            class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6 text-color"
            style="font-size: 20px !important;"
          >
            {{ $t('signup.stakeholders.title') }}
          </h2>
          <v-breadcrumbs divider=">" :items="breadcrumbs" class="py-2" />
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <!------------------------------------------>
          <!-- CREATE STAKEHOLDER -------------------->
          <!------------------------------------------>
          <v-btn
            color="#2661FF"
            class="rounded-lg mb-2"
            @click="openCreateStakeHolderDialog"
          >
            <v-icon color="white">
              add
            </v-icon>
            <span style="color: white;">
              {{ $t('signup.stakeholders.create.title') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- SENDER MANAGER --------------------->
      <!------------------------------------------>
      <NotificationManager class="mt-4" />
      <!------------------------------------------>
      <!-- VIEW STAKEHOLDERS --------------------->
      <!------------------------------------------>
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="rounded-lg custom_card" flat>
            <v-card-title class="text-color mt-2 ml-1">
              <h2
                class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6 text-color"
                style="font-size: 20px !important;"
              >
                {{ $t('signup.stakeholders.title') }}
              </h2>
            </v-card-title>
            <v-card-text class="px-10">
              <v-row>
                <v-col cols="12" class="d-flex justify-start align-center">
                  <p class="text-color text-body-2 pl-2">
                    {{ $t('signup.stakeholders.description') }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
              <v-row no-gutters>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="stakeholdersTable.filters.search"
                    outlined
                    class="rounded-lg font-weight-regular mx-5"
                    :label="$t('actions.search')"
                    prepend-inner-icon="search"
                    clearable
                    @keyup.enter="fetchStakeholders"
                    @click:clear="clearSearch"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    :loading="stakeholdersTable.loading"
                    :headers="stakeholdersTable.headers"
                    :items="stakeholdersTable.items"
                    :server-items-length="stakeholdersTable.total"
                    :items-per-page="stakeholdersTable.pagination.itemsPerPage"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20]
                    }"
                    :no-data-text="$t('subscriptions_report.no_data')"
                    :no-results-text="$t('subscriptions_report.no_data')"
                    @update:options="stakeholdersTable.pagination = $event"
                  >
                    <!-- SHOWS NO DATA -->
                    <template v-slot:no-data>
                      <div class="text-center pa-5 my-5">
                        <v-img
                          :src="require('@/assets/svg/box_empty.svg')"
                          height="150"
                          max-height="150px"
                          aspect-ratio="1"
                          contain
                        />
                        <div class="grey--text">
                          {{ $t('subscriptions_report.no_data') }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.id="{ item }">
                      {{ item.id }}
                    </template>
                    <template v-slot:item.name="{ item }">
                      {{ item.email }}
                    </template>
                    <template v-slot:item.createdAt="{ item }">
                      {{ item.createdAt | moment('lll') }}
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex justify-center align-center">
                        <v-btn icon @click="openEditStakeholderDialog(item)">
                          <v-icon>
                            edit_square
                          </v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          @click="openDeleteStakeholderDialog(item.id)"
                          :loading="deleteDialog.loading"
                        >
                          <v-icon>
                            delete
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <!------------------------------------------>
    <!-- CREATE OR EDIT STAKEHOLDER ------------>
    <!------------------------------------------>
    <HandleStakeholderDialog
      v-if="handleStakeholderDialog.display"
      :value="handleStakeholderDialog"
      @fetchStakeholders="fetchStakeholders"
      @close="handleStakeholderDialog.display = false"
      @update:display="handleStakeholderDialog.display = $event"
    />
    <!------------------------------------------>
    <!-- DELETE STAKEHOLDER --------------------->
    <!------------------------------------------>
    <BaseConfirmDialog
      v-if="deleteDialog.display"
      :cancel-text="$t('general.cancel')"
      :accept-text="$t('general.delete')"
      @close="deleteDialog = false"
      @proceed="confirmRemove()"
      :title="$t('signup.stakeholders.delete.title')"
      :description="
        $t('signup.stakeholders.delete.description', {
          id: deleteDialog.id
        })
      "
    />
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import HandleStakeholderDialog from '@/components/Signup/Stakeholders/HandleStakeholderDialog'
import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue'
import NotificationManager from '../../components/Signup/Notifications/NotificationManager.vue'

export default {
  name: 'Stakeholders',
  components: {
    HandleStakeholderDialog,
    BaseConfirmDialog,
    NotificationManager
  },
  data() {
    return {
      loading: false,
      breadcrumbs: [
        {
          text: 'Signup',
          disabled: true
        },
        {
          text: this.$t('signup.stakeholders.title'),
          disabled: false
        }
      ],
      handleStakeholderDialog: {
        display: false,
        loading: false,
        type: 'create',
        stakeholder: {}
      },
      deleteDialog: {
        id: null,
        display: false,
        loading: false
      },
      stakeholdersTable: {
        loading: false,
        items: [],
        total: 0,
        filters: {
          search: ''
        },
        pagination: {
          sortBy: ['id'],
          sortDesc: [false],
          page: 1,
          itemsPerPage: 10
        },
        headers: [
          {
            text: 'ID',
            align: 'center',
            sortable: true,
            value: 'id',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('common.email'),
            align: 'center',
            sortable: true,
            value: 'email',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('common.created_at'),
            align: 'center',
            sortable: true,
            value: 'createdAt',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('common.actions'),
            align: 'center',
            sortable: false,
            value: 'actions',
            class: 'text-no-wrap header-color'
          }
        ]
      }
    }
  },
  watch: {
    'stakeholdersTable.pagination': {
      async handler() {
        await this.fetchStakeholders()
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      stakeholders: state => state.signup.stakeholders,
      stakeholdersTotal: state => state.signup.stakeholdersTotal
    })
  },
  methods: {
    ...mapActions({
      removeStakeholder: 'signup/removeStakeholder',
      fetchStakeholdersAction: 'signup/fetchStakeholders'
    }),
    async fetchStakeholders() {
      const { stakeholdersTable } = this
      const { search } = stakeholdersTable.filters
      const { page, itemsPerPage } = stakeholdersTable.pagination
      let { sortBy, sortDesc } = stakeholdersTable.pagination

      stakeholdersTable.loading = true

      if (Array.isArray(sortBy)) {
        ;[sortBy] = sortBy
      }
      if (Array.isArray(sortDesc)) {
        ;[sortDesc] = sortDesc
      }

      const payload = {
        page,
        sortBy,
        search,
        sortDesc,
        limit: itemsPerPage
      }

      await this.fetchStakeholdersAction(payload)

      stakeholdersTable.items = this.stakeholders
      stakeholdersTable.total = parseInt(this.stakeholdersTotal, 10)

      stakeholdersTable.loading = false
    },
    openCreateStakeHolderDialog() {
      this.handleStakeholderDialog.display = true
      this.handleStakeholderDialog.type = 'create'
    },
    openEditStakeholderDialog(stakeholder) {
      this.handleStakeholderDialog.display = true
      this.handleStakeholderDialog.type = 'edit'
      this.handleStakeholderDialog.stakeholder = stakeholder
    },
    openDeleteStakeholderDialog(id) {
      this.deleteDialog.id = id
      this.deleteDialog.display = true
    },
    async confirmRemove() {
      const { deleteDialog } = this
      deleteDialog.loading = true
      await this.removeStakeholder(deleteDialog.id)
      deleteDialog.loading = false
      this.deleteDialog.display = false
      await this.fetchStakeholders()
    },
    clearSearch() {
      this.stakeholdersTable.filters.search = ''
      this.fetchStakeholders()
    }
  }
}
</script>

<style lang="scss">
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.text-color {
  color: #344054 !important;
}

.header-color {
  color: #2661ff !important;
}
</style>
